/* eslint-disable react-hooks/rules-of-hooks */
import ArticleCard from "@blocks/Card/ArticleCard";
import MainBlock from "@container/MainBlock";
import MainContainer from "@container/MainContainer";
import NdSideBlock from "@container/SideBlock/NdSideBlock";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import { Imu1CompanionAdsInfiniteScroll } from "@elements/Advertisement/variant/AdsCompanion";
import { SpecialHeader } from "@header/SpecialHeader";
import fetchTMApi from "@helper/fetchTMApi";
import { displayAfter, hideAfter } from "@helper/utils";
import {
  CreateLoadMoreHandler,
  type CreateLoadMoreHandlerProps,
} from "@hooks/useLoadMore/CreateLoadMoreHandler";
import LoadMore from "@hooks/useLoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@hooks/useLoadMore/SetFirstRenderedArticleIds";
import { useMobWebView } from "@hooks/useMobWebView";
import ErrorPage from "@pages/ErrorPage";
import { KeywordMetaData } from "@pages/Keyword/KeywordMetaData";
import Budget2025 from "@pages/Keyword/Variants/Budget2025";
import Deepavali from "@pages/Keyword/Variants/Deepavali";
import Ndp2025 from "@pages/Keyword/Variants/Ndp2025";
import NotFound from "@pages/NotFound";
import type { TRouteWithoutRedirect } from "@sphtech/web2-core/ssr";
import {
  CustomContext,
  ResponseType,
  useRouteContext,
} from "@sphtech/web2-core/ssr";
import { type ProcessedArticleData } from "@transformer/useOsResponse";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import { KeywordPayloadResponseType } from "@typings/PagePayloadType";
import { Fragment, useState } from "react";

const PAGE_SIZE = 5;
const INITIAL_ARTICLE_COUNT = 5;

export default function KeywordPage() {
  const routeContext: CustomContext<
    TRouteWithoutRedirect<KeywordPayloadResponseType, string>
  > = useRouteContext();
  const dataLoaderResponse = routeContext.context;
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const keywordArticles = dataLoaderResponse.payload.data;
  const tagAlias = dataLoaderResponse.payload.tagAlias;
  const liveBlog = dataLoaderResponse.payload.liveBlog;
  const ndpVariant = dataLoaderResponse.payload.ndpVariant;
  const recordedVideo = dataLoaderResponse.payload.recordedVideo;
  const mobwebviewBool = useMobWebView();

  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);

  const showLoadMore: boolean = keywordArticles.length == INITIAL_ARTICLE_COUNT;
  useSetFirstRenderedArticleIds(keywordArticles, setRenderedArticleIds);

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length == 0 ? keywordArticles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn: tagAlias,
      sort: lastLoadedArticle?.sort,
    };

    const fetchMoreTagsStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[] | []> => {
      const queryParams = new URLSearchParams({
        tagAlias: requestData.filterArticlesBasedOn as string,
        sort: Array.isArray(requestData.sort) ? requestData.sort.join(",") : "",
        size: "5",
      }).toString();
      const response: ProcessedArticleData[] = await fetchTMApi(
        `more-tags-stories?${queryParams}`,
        "GET",
      );

      return response;
    };
    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction: fetchMoreTagsStoriesData,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: PAGE_SIZE,
      pageNumber,
    };
    const response = await CreateLoadMoreHandler(createLoadMoreProps);
    return response;
  };
  const secondaryTagAlias = tagAlias === "deepavali-2025" ? "deepavali" : ""; // Added to handle Deepavali and Deepavali 2025 tag names
  let tagName = "";
  for (const tag of keywordArticles[0].tags || []) {
    if (
      tag.alias?.toLowerCase().includes(tagAlias.toLowerCase()) ||
      (secondaryTagAlias != "" &&
        tag.alias?.toLowerCase().includes(secondaryTagAlias.toLowerCase()))
    ) {
      if (tag.name !== undefined) {
        tagName = tag.name;
        break;
      }
    }
  }
  let showBreadcrumbs = true;
  let showTitle = true;
  const specialTags = [
    "தேசிய தினம் 2024",
    "தேசிய தினம் 2025",
    "பட்ஜெட் 2025",
    "தீபாவளி 2025",
    "தீபாவளி",
  ];
  if (specialTags.includes(tagName)) {
    showBreadcrumbs = false;
    showTitle = false;
  }
  const pageAdTargetValue = PageAdTargetingTypeEnum.TAG;
  const sectionName = "keywords";

  const { gaData, metaTags } = KeywordMetaData({
    tagName,
    tagAlias,
  });

  return (
    <MainContainer
      gaData={gaData}
      metaTagsProps={metaTags}
      sectionName={
        tagName === "தேசிய தினம் 2025" ? "national-day-2025" : sectionName
      }
      pageAdTargetValue={pageAdTargetValue}
      mobwebview={mobwebviewBool}
    >
      {specialTags.includes(tagName) && <SpecialHeader title={tagName} />}

      {specialTags.includes(tagName) ? (
        <>
          {tagName === "பட்ஜெட் 2025" && (
            <Budget2025
              tagAlias={tagAlias}
              tagName={tagName}
              sectionName={sectionName}
              keywordArticles={keywordArticles}
              showBreadcrumbs={showBreadcrumbs}
              showTitle={showTitle}
              pageAdTargetValue={pageAdTargetValue}
            />
          )}
          {tagName === "தேசிய தினம் 2025" && (
            <Ndp2025
              tagAlias={tagAlias}
              tagName={tagName}
              sectionName={sectionName}
              keywordArticles={keywordArticles}
              showBreadcrumbs={showBreadcrumbs}
              showTitle={showTitle}
              pageAdTargetValue={pageAdTargetValue}
              liveBlog={liveBlog}
              ndpVariant={ndpVariant}
            />
          )}

          {(tagName === "தீபாவளி 2025" || tagName === "தீபாவளி") && (
            <Deepavali
              tagAlias={tagAlias}
              tagName={tagName}
              sectionName={sectionName}
              keywordArticles={keywordArticles}
              showBreadcrumbs={showBreadcrumbs}
              showTitle={showTitle}
              pageAdTargetValue={pageAdTargetValue}
              recordedVideo={recordedVideo}
            />
          )}
        </>
      ) : (
        <WrapperBlock className="flex flex-col lg:flex-row justify-between">
          <MainBlock
            sectionName={tagName}
            showBreadcrumbs={showBreadcrumbs}
            showTitle={showTitle}
          >
            {[...keywordArticles, ...loadMoreData].map((section, index) => (
              <Fragment key={section.id}>
                <ArticleCard
                  displayWidth={260}
                  displayHeight={192}
                  elements={{ hasDate: true }}
                  {...section}
                  overrideStyle={{
                    cardWrapper:
                      (displayAfter(index + 1, PAGE_SIZE)
                        ? "border-none"
                        : "flex-col lg:flex-row") +
                      (hideAfter(index, PAGE_SIZE)
                        ? " lg:flex-row flex-row-reverse"
                        : ""),
                    titleStyle: hideAfter(index, PAGE_SIZE)
                      ? "font-semibold text-2sm text-gray-100 hover:text-gray-100 lg:text-2lg lg:text-red-100 lg:hover:text-red-200"
                      : "text-md lg:text-2lg",
                    paragraphStyle: hideAfter(index, PAGE_SIZE)
                      ? "hidden lg:block"
                      : "",
                    imageWrapperStyle: hideAfter(index, PAGE_SIZE)
                      ? "w-[90px] h-[90px] lg:h-auto"
                      : "",
                    imageStyle: hideAfter(index, PAGE_SIZE)
                      ? "h-[90px] lg:h-auto"
                      : "",
                  }}
                />
                {displayAfter(index + 1, PAGE_SIZE) && (
                  <Imu1CompanionAdsInfiniteScroll
                    sectionName={sectionName}
                    index={(index + 1) / PAGE_SIZE}
                    pageAdTargetType={pageAdTargetValue}
                  />
                )}
              </Fragment>
            ))}
            {showLoadMore ? (
              <div className="flex w-full justify-center mb-xl">
                <LoadMore
                  rootClassName="my-4 w-full"
                  onLoadMore={handleLoadMore}
                  hasMore={hasMoreStories}
                  loadText="Load More"
                  mobileInfiniteScroll={true}
                  pageNumber={pageNumber}
                  sectionName={sectionName}
                />
              </div>
            ) : null}
          </MainBlock>
          {tagName === "தேசிய தினம் 2024" ? (
            <NdSideBlock
              sectionName={tagName}
              className="w-full lg:block"
              pageAdTargetValue={pageAdTargetValue}
              hideAdForMobile={true}
            />
          ) : (
            <SideBlock
              sectionName={tagName}
              className="w-full lg:block"
              pageAdTargetValue={pageAdTargetValue}
              hideAdForMobile={true}
            />
          )}
        </WrapperBlock>
      )}
    </MainContainer>
  );
}
